import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import OptionCards, { OptionCard } from './index';

import styles from './filterable.module.scss';

const searchIcon = new URL('../../../img/search.svg', import.meta.url).href;

interface FilterableOptionCardsProps {
  options: OptionCard[];
  value: Array<string | number> | string | number | null;
  onChange: (value: string | number) => void;
  columns?: number;
  /** Начиная с этого количества опций показываем поиск */
  searchThreshold?: number;
  /** Сколько карточек видно до нажатия «Показать ещё» */
  visibleLimit?: number;
}

const asArray = (value: FilterableOptionCardsProps['value']) =>
  Array.isArray(value) ? value : [value];

/**
 * Длинный справочник карточками: поиск по названию и сворачивание списка,
 * чтобы шаг формы не превращался в бесконечную сетку.
 */
const FilterableOptionCards = ({
  options,
  value,
  onChange,
  columns = 3,
  searchThreshold = 12,
  visibleLimit = 12,
}: FilterableOptionCardsProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const selected = asArray(value);
  const withSearch = options.length >= searchThreshold;

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return options;
    return options.filter((option) => option.label.toLowerCase().indexOf(search) !== -1);
  }, [options, query]);

  // В свёрнутом виде выбранные карточки показываем всегда — иначе выбор не видно
  const visible = useMemo(() => {
    if (isExpanded || query.trim() || filtered.length <= visibleLimit) return filtered;
    const head = filtered.slice(0, visibleLimit);
    const rest = filtered
      .slice(visibleLimit)
      .filter((option) => selected.some((item) => String(item) === String(option.value)));
    return head.concat(rest);
  }, [filtered, isExpanded, query, visibleLimit, selected]);

  const hiddenCount = filtered.length - visible.length;

  return (
    <div className={styles.wrapper}>
      {withSearch && (
        <label className={styles.search}>
          <img className={styles.searchIcon} src={searchIcon} alt="" />
          <input
            type="text"
            className={styles.searchInput}
            value={query}
            placeholder={t('transportFields.steps.search_placeholder')}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      )}

      {visible.length > 0 ? (
        <OptionCards options={visible} columns={columns} value={value} onChange={onChange} />
      ) : (
        <p className={styles.empty}>{t('transportFields.steps.nothing_found')}</p>
      )}

      {hiddenCount > 0 && (
        <button type="button" className={styles.toggle} onClick={() => setIsExpanded(true)}>
          {t('transportFields.steps.show_more', { count: hiddenCount })}
        </button>
      )}
      {isExpanded && !query.trim() && filtered.length > visibleLimit && (
        <button type="button" className={styles.toggle} onClick={() => setIsExpanded(false)}>
          {t('transportFields.steps.collapse')}
        </button>
      )}
    </div>
  );
};

export default FilterableOptionCards;
